import { useEffect, useRef, useState } from "react";
import { Box, Button, Typography, Link, TextField, FormControlLabel, Checkbox } from "@mui/material";
import axios from "axios";

import api from "../api/api";

import BackgroundImage from "../assets/img/main-bg-pg.svg";
import LoginImage from "../assets/img/login-page-img.svg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";

import { useLocation, useNavigate } from "react-router-dom";
import "../assets/style/LoginScreen.css";
import usePwaInstallStatus, {
	forgetPwaInstalled,
	forgetPwaInstallDismissed,
	hasPwaInstallDismissed,
	installPromptKey,
	isIOSDevice,
	rememberPwaInstallDismissed,
} from "../hooks/usePwaInstallStatus";

// Компонент для SVG-иконки в логотипе
const LogoIcon = (props) => (
	<svg fill="none" height="100%" viewBox="0 0 24 24" width="100%" xmlns="http://www.w3.org/2000/svg" {...props}>
		<path
			opacity="0.16"
			d="M7.242 11.083c.449-1.674 2.17-3.394 3.843-3.843l10.434-2.796c1.673-.448 2.666.545 2.218 2.218L20.94 17.096c-.449 1.674-2.17 3.394-3.843 3.843L6.664 23.735c-1.673.448-2.666-.545-2.218-2.218l2.796-10.434Z"
			fill="#6366F1"
		></path>
		<path
			d="M3.06 6.9c.448-1.674 2.168-3.394 3.842-3.843L17.336.261c1.673-.448 2.667.545 2.218 2.218l-2.796 10.434c-.449 1.674-2.169 3.394-3.843 3.843L2.481 19.552C.808 20-.185 19.007.263 17.334L3.06 6.9Z"
			fill="#6366F1"
		></path>
	</svg>
);

const LottieAnimation = () => (
	<Box sx={{ width: "70%", margin: "0px auto" }}>
		<img src={LoginImage} alt="" style={{ width: "100%" }} />
	</Box>
);

const apiAddress = "https://api.palladiumwallet.st";
const installQueryParam = "install";
const iphoneInstallHint = "Open this page in Safari, tap Share, then Add to Home Screen.";
const turnstileEnabled = process.env.REACT_APP_TURNSTILE_ENABLED === "true";
const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const turnstileScriptUrl = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let turnstileScriptPromise;

const isInstallLink = () => {
	if (typeof window === "undefined") {
		return false;
	}

	return new URLSearchParams(window.location.search).get(installQueryParam) === "1";
};

const loadTurnstileScript = () => {
	if (typeof window === "undefined") {
		return Promise.reject(new Error("Turnstile can only be loaded in the browser."));
	}

	if (window.turnstile) {
		return Promise.resolve(window.turnstile);
	}

	if (turnstileScriptPromise) {
		return turnstileScriptPromise;
	}

	turnstileScriptPromise = new Promise((resolve, reject) => {
		const existingScript = document.querySelector(`script[src="${turnstileScriptUrl}"]`);

		if (existingScript) {
			existingScript.addEventListener("load", () => resolve(window.turnstile), { once: true });
			existingScript.addEventListener("error", reject, { once: true });
			return;
		}

		const script = document.createElement("script");
		script.src = turnstileScriptUrl;
		script.async = true;
		script.defer = true;
		script.onload = () => resolve(window.turnstile);
		script.onerror = reject;
		document.head.appendChild(script);
	});

	return turnstileScriptPromise;
};

const TurnstileCaptcha = ({ action, onVerify, onExpire, onError, resetSignal }) => {
	const containerRef = useRef(null);
	const widgetIdRef = useRef(null);
	const callbacksRef = useRef({ onVerify, onExpire, onError });

	useEffect(() => {
		callbacksRef.current = { onVerify, onExpire, onError };
	}, [onVerify, onExpire, onError]);

	useEffect(() => {
		let isCancelled = false;

		if (!turnstileSiteKey) {
			callbacksRef.current.onError("Cloudflare Turnstile site key is not configured.");
			return undefined;
		}

		loadTurnstileScript()
			.then((turnstile) => {
				if (isCancelled || !containerRef.current || !turnstile) {
					return;
				}

				widgetIdRef.current = turnstile.render(containerRef.current, {
					sitekey: turnstileSiteKey,
					action,
					theme: "dark",
					appearance: "always",
					callback: (token) => callbacksRef.current.onVerify(token),
					"expired-callback": () => callbacksRef.current.onExpire(),
					"timeout-callback": () => callbacksRef.current.onExpire(),
					"error-callback": () => callbacksRef.current.onError("Cloudflare verification failed. Please try again."),
				});
			})
			.catch(() => callbacksRef.current.onError("Cloudflare verification could not be loaded."));

		return () => {
			isCancelled = true;

			if (window.turnstile && widgetIdRef.current !== null) {
				window.turnstile.remove(widgetIdRef.current);
				widgetIdRef.current = null;
			}
		};
	}, [action]);

	useEffect(() => {
		if (window.turnstile && widgetIdRef.current !== null) {
			window.turnstile.reset(widgetIdRef.current);
		}
	}, [resetSignal]);

	return (
		<div className="turnstile-wrapper">
			{turnstileSiteKey ? (
				<div ref={containerRef} className="turnstile-widget" />
			) : (
				<Typography className="captcha-config-error">Cloudflare Turnstile site key is not configured.</Typography>
			)}
		</div>
	);
};

const LoginScreen = () => {
	const [formState, setFormState] = useState("login");

	const [confirmPassword, setConfirmPassword] = useState("");
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [USCitizen, setUSCitizen] = useState(false);

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [verificationCode, setVerificationCode] = useState("");
	const [verificationExpiresAt, setVerificationExpiresAt] = useState("");
	const [captchaTokens, setCaptchaTokens] = useState({ login: "", register: "" });
	const [captchaErrors, setCaptchaErrors] = useState({ login: "", register: "" });
	const [captchaResetSignal, setCaptchaResetSignal] = useState({ login: 0, register: 0 });
	const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
	const { isAppInstalled, markInstalled, setIsAppInstalled } = usePwaInstallStatus();
	const [isInstallDismissed, setIsInstallDismissed] = useState(hasPwaInstallDismissed);
	const [installMode, setInstallMode] = useState(isInstallLink);
	const [installHint, setInstallHint] = useState("");
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		const handleBeforeInstallPrompt = (event) => {
			event.preventDefault();
			forgetPwaInstalled();
			window[installPromptKey] = event;
			setDeferredInstallPrompt(event);
			setIsAppInstalled(false);

			if (installMode) {
				setInstallHint("The browser install banner should appear. If it does not, tap Install app.");
				return;
			}

			setInstallHint("");
		};

		const handleAppInstalled = () => {
			window[installPromptKey] = null;
			setDeferredInstallPrompt(null);
			markInstalled();
			setInstallHint("PalladiumWallet is installed.");
		};

		window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
		window.addEventListener("appinstalled", handleAppInstalled);

		return () => {
			window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
			window.removeEventListener("appinstalled", handleAppInstalled);
		};
	}, [installMode, markInstalled, setIsAppInstalled]);

	useEffect(() => {
		const nextInstallMode = new URLSearchParams(location.search).get(installQueryParam) === "1";
		setInstallMode(nextInstallMode);

		if (nextInstallMode) {
			forgetPwaInstallDismissed();
			setIsInstallDismissed(false);
		}
	}, [location.search]);

	useEffect(() => {
		if (!installMode || isAppInstalled) {
			return;
		}

		setInstallHint(isIOSDevice() ? "Open this page in Safari, tap Share, then Add to Home Screen." : "Preparing the install request...");

		const hintTimeout = window.setTimeout(() => {
			setInstallHint((currentHint) => currentHint === "Preparing the install request..." ? "If the browser does not show an install request, open the browser menu and choose Install app." : currentHint);
		}, 1800);

		return () => window.clearTimeout(hintTimeout);
	}, [installMode, isAppInstalled]);

	useEffect(() => {
		if (!installMode || !isAppInstalled) {
			return;
		}

		setInstallMode(false);
		setInstallHint("");
		navigate("/", { replace: true });
	}, [installMode, isAppInstalled, navigate]);

	const exitInstallMode = (event) => {
		event?.preventDefault();
		rememberPwaInstallDismissed();
		setIsInstallDismissed(true);
		setInstallMode(false);
		setInstallHint("");
		navigate("/", { replace: true });
	};

	const handleInstallApp = async () => {
		if (isAppInstalled) {
			setInstallHint("PalladiumWallet is already installed.");
			return;
		}

		if (deferredInstallPrompt) {
			try {
				await deferredInstallPrompt.prompt();
				const choice = await deferredInstallPrompt.userChoice;

				setDeferredInstallPrompt(null);
				if (choice.outcome === "accepted") {
					setInstallHint("");
				} else {
					rememberPwaInstallDismissed();
					setIsInstallDismissed(true);
					setInstallMode(false);
					setInstallHint("");
					if (installMode) {
						navigate("/", { replace: true });
					}
				}
			} catch (promptError) {
				console.error("PWA install prompt failed:", promptError);
				setDeferredInstallPrompt(null);
				setInstallHint("Use the browser install option to add PalladiumWallet to this device.");
			}

			return;
		}

		setInstallHint(isIOSDevice() ? "Open this page in Safari, tap Share, then Add to Home Screen." : "Use the browser install option to add PalladiumWallet to this device.");
	};

	const handleIphoneInstall = (event) => {
		event?.preventDefault();
		event?.stopPropagation();
		setInstallHint(iphoneInstallHint);
	};

	// 3. Функция для обработки отправки формы
	const handleLoginSubmit = async (event) => {
		event.preventDefault(); // Предотвращаем перезагрузку страницы
		setLoading(true); // Включаем состояние загрузки (кнопка будет крутиться)
		setError(""); // Сбрасываем предыдущие ошибки
		setSuccessMessage("");

		// Формируем тело запроса
		if (!validateCaptcha("login")) {
			setLoading(false);
			return;
		}

		const requestBody = {
			email: email,
			password: password,
			...(turnstileEnabled ? {
				"cf-turnstile-response": captchaTokens.login,
				turnstile_token: captchaTokens.login,
			} : {}),
		};

		try {
			// 4. Отправляем POST-запрос
			const response = await axios.post(`${apiAddress}/api/auth/login`, requestBody, {
				headers: { Accept: "application/json" },
			});
			console.log("Успешный вход!", response.data);
			sessionStorage.setItem("accessToken", response.data.token);
			sessionStorage.setItem("client_id", response.data.client.id);

			if (response.data.email_verification_required) {
				setVerificationCode("");
				setVerificationExpiresAt(response.data.email_verification_expires_at || "");
				setSuccessMessage(`We sent a 6-digit verification code to ${response.data.client.email}.`);
				setFormState("verifyEmail");
				return;
			}

			// <Navigate to="/login" replace />;
			navigate("/dashboard");
		} catch (err) {
			// 5. В случае ошибки
			console.error("Ошибка входа:", err.response);
			// Устанавливаем сообщение об ошибке, чтобы показать его пользователю
			setError(err.response?.data?.message || "Incorrect E-Mail or password");
			resetCaptcha("login");
		} finally {
			// 6. В любом случае выключаем состояние загрузки
			setLoading(false);
		}
	};

	const handleRegisterSubmit = async (event) => {
		event.preventDefault();

		// 1. Клиентская валидация
		if (password !== confirmPassword) {
			setError("Passwords do not match.");
			return;
		}
		if (!termsAccepted) {
			setError("You must accept the Terms of Service.");
			return;
		}

		if(!USCitizen) {
			setError('Please check the box "I\'m not a U.S. citizen or resident."');
			return;
		}

		setLoading(true);
		setError("");
		setSuccessMessage("");

		if (!validateCaptcha("register")) {
			setLoading(false);
			return;
		}

		const requestBody = {
			email: email,
			password: password,
			...(turnstileEnabled ? {
				"cf-turnstile-response": captchaTokens.register,
				turnstile_token: captchaTokens.register,
			} : {}),
		};

		try {
			const response = await api.post(`/auth/register`, requestBody);

			console.log("Успешная регистрация!", response.data);
			const { token, client } = response.data;

			// Сохраняем токен в sessionStorage для консистентности
			sessionStorage.setItem("accessToken", token);
			sessionStorage.setItem("client_id", client.id);

			if (response.data.email_verification_required) {
				setVerificationCode("");
				setVerificationExpiresAt(response.data.email_verification_expires_at || "");
				setSuccessMessage(`We sent a 6-digit verification code to ${client.email}.`);
				setFormState("verifyEmail");
				return;
			}

			navigate("/dashboard");
		} catch (err) {
			console.error("Ошибка регистрации:", err.response.data);

			if (err.response && err.response.status === 422) {
				const errors = err.response.data.errors;

				const firstErrorKey = Object.keys(errors)[0];
				const errorMessage = errors[firstErrorKey][0];
				setError(errorMessage);
			} else {
				setError("An unknown error occurred during registration.");
			}
			resetCaptcha("register");
		} finally {
			setLoading(false);
		}
	};

	const handleForgotSubmit = async (event) => {
		event.preventDefault();
		setLoading(true);
		setError("");
		setSuccessMessage("");

		try {
			await api.post("/auth/forgot-password", { email });

			alert("A password reset link has been sent to your email address.");
			setFormState("login");
		} catch (err) {
			setError(err.response?.data?.error || "Failed to send reset link.");
			console.log(err.response?.data.error);
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyEmailSubmit = async (event) => {
		event.preventDefault();
		setLoading(true);
		setError("");
		setSuccessMessage("");

		try {
			const response = await api.post("/auth/verify-email-code", {
				code: verificationCode.replace(/\D/g, "").slice(0, 6),
			});

			if (response.data?.client?.id) {
				sessionStorage.setItem("client_id", response.data.client.id);
			}

			navigate("/dashboard");
		} catch (err) {
			setError(err.response?.data?.message || "Failed to verify email.");
		} finally {
			setLoading(false);
		}
	};

	const handleResendVerificationCode = async () => {
		setLoading(true);
		setError("");
		setSuccessMessage("");

		try {
			const response = await api.post("/auth/resend-email-verification-code");
			setVerificationExpiresAt(response.data.email_verification_expires_at || "");
			setSuccessMessage("A new verification code has been sent to your email.");
		} catch (err) {
			setError(err.response?.data?.message || "Failed to resend verification code.");
		} finally {
			setLoading(false);
		}
	};

	const switchFormState = (newState) => {
		setFormState(newState);
		setError("");
		setSuccessMessage("");
	};

	const formatVerificationExpiry = (value) => {
		if (!value) {
			return "";
		}

		const date = new Date(value);

		if (Number.isNaN(date.getTime())) {
			return "";
		}

		return date.toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const handleCaptchaVerify = (formName, token) => {
		setCaptchaTokens((tokens) => ({ ...tokens, [formName]: token }));
		setCaptchaErrors((errors) => ({ ...errors, [formName]: "" }));
	};

	const resetCaptcha = (formName) => {
		setCaptchaTokens((tokens) => ({ ...tokens, [formName]: "" }));
		setCaptchaResetSignal((signals) => ({ ...signals, [formName]: signals[formName] + 1 }));
	};

	const handleCaptchaExpire = (formName) => {
		setCaptchaTokens((tokens) => ({ ...tokens, [formName]: "" }));
		setCaptchaErrors((errors) => ({ ...errors, [formName]: "Cloudflare verification expired. Please try again." }));
	};

	const handleCaptchaError = (formName, message) => {
		setCaptchaTokens((tokens) => ({ ...tokens, [formName]: "" }));
		setCaptchaErrors((errors) => ({ ...errors, [formName]: message }));
	};

	const validateCaptcha = (formName) => {
		if (!turnstileEnabled) {
			return true;
		}

		if (!turnstileSiteKey) {
			const message = "Cloudflare Turnstile site key is not configured.";
			setCaptchaErrors((errors) => ({ ...errors, [formName]: message }));
			setError(message);
			return false;
		}

		if (!captchaTokens[formName]) {
			const message = "Please complete the Cloudflare verification.";
			setCaptchaErrors((errors) => ({ ...errors, [formName]: message }));
			setError(message);
			return false;
		}

		return true;
	};

	return (
		<div className="login-screen-container">
			<div className="login-left-panel" style={{ backgroundImage: `url(${BackgroundImage})` }}>
				<div className="login-left-content">
					<Typography variant="h4" className="login-title">
						Welcome to PalladiumWallet
					</Typography>
					<Typography className="login-subtitle">The all-in-one solution for your cryptocurrency wallet, wallet management, and wallet tracking needs.</Typography>
					<LottieAnimation />
				</div>
			</div>

			<div className="login-right-panel">
				<div className="login-form-container">
					<a href="/" className="logo-link" onClick={exitInstallMode}>
						<div className="logo-icon-wrapper">
							<LogoIcon />
						</div>
						<Typography variant="h6" className="logo-text">
							PalladiumWallet
						</Typography>
					</a>

					<div className="login-header">
						{formState === "forgotPassword" || formState === "verifyEmail" ? (
							<div className="forgot-password-header">
								<div className="forgot-password-back-btn">
									<Link component="button" onClick={() => switchFormState(formState === "verifyEmail" ? "register" : "login")} className="go-back-link">
										<ArrowBackIcon />
										<span>Go Back</span>
									</Link>
								</div>

								<div className="login-header">
									<Typography variant="h5" className="login-header-title">
										{formState === "verifyEmail" ? "Confirm your email" : "Forgot password"}
									</Typography>
									<Typography className="login-header-subtitle">
										{formState === "verifyEmail"
											? "Enter the 6-digit code from your email to finish registration."
											: "Enter the email address associated with your account."}
									</Typography>
								</div>
							</div>
						) : (
							<div className="login-header">
								<Typography variant="h5" className="login-header-title">
									{formState === "register" ? "Create an account" : "Log in"}
								</Typography>
								<Typography className="login-header-subtitle">
									{formState === "register" ? "Have an account?" : "Don't have an account?"}{" "}
									<Link component="button" onClick={() => switchFormState(formState === "login" ? "register" : "login")} className="register-link">
										{formState === "register" ? "Login" : "Register"}
									</Link>
								</Typography>
							</div>
						)}
					</div>

					{successMessage && <Typography className="success-message">{successMessage}</Typography>}

					{formState === "login" && (
						<>
							<form noValidate className="login-form" onSubmit={handleLoginSubmit}>
								<div className="form-fields">
									<TextField label="Email Address" variant="outlined" className="login-textfield" value={email} onChange={(e) => setEmail(e.target.value)} required />
									<TextField label="Password" type="password" variant="outlined" className="login-textfield" value={password} onChange={(e) => setPassword(e.target.value)} required />
								</div>
									{turnstileEnabled && (
										<TurnstileCaptcha
											action="login"
											resetSignal={captchaResetSignal.login}
											onVerify={(token) => handleCaptchaVerify("login", token)}
											onExpire={() => handleCaptchaExpire("login")}
											onError={(message) => handleCaptchaError("login", message)}
										/>
									)}
									{turnstileEnabled && captchaErrors.login && <Typography className="captcha-error-message">{captchaErrors.login}</Typography>}
								{error && <Typography className="error-message">{error}</Typography>}
								<div className="form-spacer" />
									<Button fullWidth size="large" type="submit" variant="contained" className="continue-button" loading={loading} disabled={loading || (turnstileEnabled && (!captchaTokens.login || !turnstileSiteKey))}>
									Continue
								</Button>
								<div className="forgot-password-container">
									<Link component="button" onClick={() => switchFormState("forgotPassword")} className="forgot-password-link">
										Forgot password?
									</Link>
								</div>
							</form>
							{!isAppInstalled && !isInstallDismissed && (
								<div className="app-download-card app-download-card--below-form" aria-label="Download PalladiumWallet app">
									<div className="app-download-copy">
										<span className="app-download-eyebrow">Mobile app</span>
										<Typography className="app-download-title">Download PalladiumWallet app</Typography>
									</div>
									<div className="app-download-actions">
										<button type="button" className="app-download-button app-download-button--primary" onClick={handleInstallApp}>
											<AndroidIcon className="app-download-icon" />
											<span className="app-download-label">
												<span className="app-download-platform">Android</span>
												<span className="app-download-meta">Install app</span>
											</span>
										</button>
										<button type="button" className="app-download-button" onClick={handleIphoneInstall}>
											<AppleIcon className="app-download-icon" />
											<span className="app-download-label">
												<span className="app-download-platform">iPhone</span>
												<span className="app-download-meta">Install app</span>
											</span>
										</button>
									</div>
									{installHint && (
										<Typography className={`app-download-hint${installHint === iphoneInstallHint ? " app-download-hint--alert" : ""}`}>
											{installHint}
										</Typography>
									)}
								</div>
							)}
						</>
					)}

					{formState === "register" && (
						<form noValidate className="login-form" onSubmit={handleRegisterSubmit}>
							<div className="form-fields">
								<TextField
									label="Email Address"
									variant="outlined"
									className="login-textfield"
									error={!!error}
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									helperText={error.includes("Email") ? error : ""}
								/>
								<TextField label="Password" type="password" variant="outlined" className="login-textfield" value={password} onChange={(e) => setPassword(e.target.value)} />
								<TextField
									label="Confirm Password"
									type="password"
									variant="outlined"
									className="login-textfield"
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
								/>
							</div>
							<FormControlLabel
								className="terms-checkbox"
								control={<Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />}
								label={
									<Typography>
										I have read the{" "}
										<Link href="https://palladiumwallet.st/terms_and_conditions" className="register-link" target="_blank"	rel="noopener noreferrer">
											Terms of Service
										</Link>
									</Typography>
								}
							/>

							<FormControlLabel
								className="terms-checkbox"
								control={<Checkbox checked={USCitizen} onChange={(e) => setUSCitizen(e.target.checked)} />}
								label={
									<Typography>
										I'm not a U.S. citizen or resident.
									</Typography>
								}
							/>

								{turnstileEnabled && (
									<TurnstileCaptcha
										action="register"
										resetSignal={captchaResetSignal.register}
										onVerify={(token) => handleCaptchaVerify("register", token)}
										onExpire={() => handleCaptchaExpire("register")}
										onError={(message) => handleCaptchaError("register", message)}
									/>
								)}
								{turnstileEnabled && captchaErrors.register && <Typography className="captcha-error-message">{captchaErrors.register}</Typography>}
							{error && <Typography className="error-message">{error}</Typography>}
								<Button fullWidth size="large" type="submit" variant="contained" className="continue-button" loading={loading} disabled={loading || (turnstileEnabled && (!captchaTokens.register || !turnstileSiteKey))}>
								Sign Up
							</Button>
						</form>
					)}

					{formState === "verifyEmail" && (
						<form noValidate className="login-form" onSubmit={handleVerifyEmailSubmit}>
							<div className="form-fields">
								<TextField
									label="Verification Code"
									variant="outlined"
									className="login-textfield verification-code-field"
									value={verificationCode}
									onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
									inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }}
									helperText={verificationExpiresAt ? `Code expires at ${formatVerificationExpiry(verificationExpiresAt)}` : "Enter the 6-digit code from your email."}
								/>
							</div>
							{error && <Typography className="error-message">{error}</Typography>}
							<div className="verification-actions">
								<Button fullWidth size="large" type="submit" variant="contained" className="continue-button" loading={loading} disabled={loading || verificationCode.length !== 6}>
									Verify Email
								</Button>
								<Button fullWidth size="large" type="button" variant="outlined" className="secondary-button" onClick={handleResendVerificationCode} disabled={loading}>
									Resend code
								</Button>
							</div>
						</form>
					)}

					{formState === "forgotPassword" && (
						<form noValidate className="login-form" onSubmit={handleForgotSubmit}>
							<div className="form-fields">
								<TextField
									label="Email Address"
									variant="outlined"
									className="login-textfield"
									error={!!error}
									helperText={error}
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									required
								/>
							</div>
							<div className="form-spacer" />
							<Button fullWidth size="large" type="submit" variant="contained" className="continue-button" loading={loading}>
								Send reset link
							</Button>
						</form>
					)}
				</div>
			</div>

			{installMode && !isAppInstalled && !isInstallDismissed && (
				<div className="app-install-overlay" role="dialog" aria-modal="true" aria-labelledby="app-install-title">
					<div className="app-download-card app-download-card--install-link app-install-popup" aria-label="Download PalladiumWallet app">
						<button type="button" className="app-install-close" onClick={exitInstallMode} aria-label="Close install popup">
							&times;
						</button>
						<div className="app-download-copy">
							<span className="app-download-eyebrow">Mobile app</span>
							<Typography id="app-install-title" className="app-download-title">Install PalladiumWallet</Typography>
							<Typography className="app-download-description">Add the app to this device for faster access. The browser controls the final install window.</Typography>
						</div>
						<div className="app-download-actions">
							<button type="button" className="app-download-button app-download-button--primary" onClick={handleInstallApp}>
								<AndroidIcon className="app-download-icon" />
								<span className="app-download-label">
									<span className="app-download-platform">Install app</span>
									<span className="app-download-meta">{isAppInstalled ? "Installed" : "Install now"}</span>
								</span>
							</button>
							<button type="button" className="app-download-button app-download-button--secondary" onClick={handleIphoneInstall}>
								<AppleIcon className="app-download-icon" />
								<span className="app-download-label">
									<span className="app-download-platform">Continue</span>
									<span className="app-download-meta">Install Now</span>
								</span>
							</button>
						</div>
						{installHint && (
							<Typography className={`app-download-hint${installHint === iphoneInstallHint ? " app-download-hint--alert" : ""}`}>
								{installHint}
							</Typography>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default LoginScreen;
